<script setup lang="ts">
/**
 * SaveViewModal
 *
 * Modal dialog for saving a new view or updating an existing one.
 * Name input, visibility radio cards, filter summary.
 */

import { ref, computed } from 'vue'
import { BaseModal, BaseButton } from '@/components'
import type { FilterGroup, SortConfig, SavedViewVisibility } from '../api/types'
import { getFilterSummary } from '../composables/filterAdapter'

interface Props {
    modelValue: boolean
    currentFilters?: FilterGroup
    currentSort?: SortConfig
    /** If editing an existing view, provide its name */
    existingName?: string
    existingVisibility?: SavedViewVisibility
}

const props = withDefaults(defineProps<Props>(), {
    existingVisibility: 'private',
})

const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    save: [name: string, visibility: SavedViewVisibility]
    close: []
}>()

const viewName = ref(props.existingName ?? '')
const visibility = ref<SavedViewVisibility>(props.existingVisibility)
const nameError = ref('')

const filterSummaryLines = computed(() => {
    if (!props.currentFilters) return []
    return getFilterSummary(props.currentFilters)
})

const hasFilters = computed(() => {
    return props.currentFilters && props.currentFilters.conditions.length > 0
})

const canSave = computed(() => {
    return viewName.value.trim().length > 0 && hasFilters.value
})

function onSave() {
    if (!canSave.value) {
        nameError.value = 'View name is required'
        return
    }
    nameError.value = ''
    emit('save', viewName.value.trim(), visibility.value)
}

function onClose() {
    viewName.value = ''
    nameError.value = ''
    emit('update:modelValue', false)
    emit('close')
}
</script>

<template>
    <BaseModal
        :model-value="modelValue"
        title="Save View"
        size="sm"
        @update:model-value="emit('update:modelValue', $event)"
        @close="onClose"
    >
        <div class="save-view-form" data-testid="save-view-modal">
            <!-- Name -->
            <div class="form-group">
                <label class="form-label">View Name</label>
                <input
                    v-model="viewName"
                    type="text"
                    class="form-input"
                    data-testid="view-name-input"
                    placeholder="e.g. Texas Properties, High-Priority Contacts"
                    autofocus
                    @keydown.enter="onSave"
                />
                <span v-if="nameError" class="form-error">{{ nameError }}</span>
            </div>

            <!-- Visibility -->
            <div class="form-group">
                <label class="form-label">Visibility</label>
                <div class="visibility-cards">
                    <label
                        :class="['visibility-card', { 'visibility-card--active': visibility === 'private' }]"
                    >
                        <input v-model="visibility" type="radio" value="private" class="sr-only" />
                        <span class="visibility-card__icon">🔒</span>
                        <span class="visibility-card__label">Private</span>
                        <span class="visibility-card__desc">Only you can see</span>
                    </label>
                    <label
                        :class="['visibility-card', { 'visibility-card--active': visibility === 'shared' }]"
                    >
                        <input v-model="visibility" type="radio" value="shared" class="sr-only" />
                        <span class="visibility-card__icon">👥</span>
                        <span class="visibility-card__label">Shared</span>
                        <span class="visibility-card__desc">Everyone in org</span>
                    </label>
                </div>
            </div>

            <!-- No Filters Warning -->
            <div v-if="!hasFilters" class="no-filters-warning" data-testid="no-filters-warning">
                <svg class="no-filters-warning__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>Add at least one filter before saving a view</span>
            </div>

            <!-- Filter Summary -->
            <div v-else-if="filterSummaryLines.length > 0" class="filter-summary">
                <div class="filter-summary__label">Filters to be saved:</div>
                <ul class="filter-summary__list">
                    <li v-for="(line, i) in filterSummaryLines" :key="i" class="filter-summary__item">
                        {{ line }}
                    </li>
                </ul>
            </div>
        </div>

        <template #footer>
            <BaseButton variant="secondary" size="sm" @click="onClose">Cancel</BaseButton>
            <BaseButton size="sm" :disabled="!canSave" @click="onSave" data-testid="save-view-confirm">Save View</BaseButton>
        </template>
    </BaseModal>
</template>

<style scoped>
.save-view-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.form-label {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
}

.form-input {
    padding: 0.5rem 0.75rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
}

.form-input:focus {
    border-color: var(--color-primary);
    outline: none;
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.15);
}

.form-error {
    font-size: var(--text-xs);
    color: var(--color-danger);
}

.visibility-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
}

.visibility-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.75rem;
    border: 2px solid var(--border-color);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.15s;
}

.visibility-card:hover {
    border-color: rgba(18, 192, 221, 0.3);
}

.visibility-card--active {
    border-color: var(--color-primary);
    background: rgba(18, 192, 221, 0.05);
}

.visibility-card__icon {
    font-size: 1.25rem;
}

.visibility-card__label {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
}

.visibility-card__desc {
    font-size: var(--text-xs);
    color: var(--text-muted);
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

.filter-summary {
    padding: 0.75rem;
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
}

.filter-summary__label {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--text-muted);
    margin-bottom: 0.5rem;
}

.filter-summary__list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.filter-summary__item {
    font-size: var(--text-xs);
    color: var(--text-secondary);
    padding: 0.125rem 0;
}

.filter-summary__item::before {
    content: '•';
    color: var(--color-primary);
    margin-right: 0.375rem;
}

.no-filters-warning {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    font-size: var(--text-xs);
    color: var(--color-warning, #f59e0b);
    background: rgba(245, 158, 11, 0.08);
    border: 1px solid rgba(245, 158, 11, 0.2);
    border-radius: var(--radius-md);
}

.no-filters-warning__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}
</style>
